import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PageLoader } from '@app/components';
import { setExceptionHandler, handleReloadShellConfig, currentUser, signInFailure } from '@app/store/actions';
import { onboardService, authService, basicService } from '@app/services';
import { signIn } from '@app/store/actions';
import OnboardWelcomeComponent from '@app/pages/login/components/onboardWelcome.component';
import OnboardSetPasswordComponent from '@app/pages/login/components/onboardSetPassword.component';
import OnboardSummaryComponent from '@app/pages/login/components/onboardSummary.component';
import OnboardLuckyYouComponent from '@app/pages/login/components/onboardLuckyYou.component';
import OnboardLoginAgainComponent from '@app/pages/login/components/onboardLoginAgain.component';
import { OnboardSetMfaToken } from '@app/pages/login/components/onboardSetMfaToken.component';
import { IPasswordPolicy } from '@app/models';
import { hasPermission } from '@app/helpers';
import { Administrator } from '@app/store/constants';
import { useTypedTranslation } from '@app/hooks';
import { AxiosResponse } from 'axios';

enum steps {
    welcome,
    luckyYou,
    setPassword,
    setMfa,
    summary,
    loginAgain
}

const OnboardPage = (props: any) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const loggedIn = useSelector((state: any) => state.signIn.loggedIn);
    const loginError = useSelector((state: any) => state.signIn.error);
    const shellConfig = useSelector((state: any) => state.basic.shellConfig);
    const currentUserData = useSelector((state: any) => state.signIn.currentUser);

    const obt: string = props.match.params.id;
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [onStep, setOnStep] = useState<steps>(steps.welcome);
    const [passwordSet, setPasswordSet] = useState(false);
    const [resetPassword, setResetPassword] = useState(false);
    const [syncSourceSet, setSyncSourceSet] = useState(false);
    const [configureMfa, setConfigureMfa] = useState(false);
    const [isPasswordReset, setIsPasswordReset] = useState(false);
    const [displayName, setDisplayName] = useState('');
    const [isOnboardingCompleting, setIsOnboardingCompleting] = useState(false);
    const [mfaReset, setMfaReset] = useState(false);
    const [passwordPolicy, setPasswordPolicy] = useState<IPasswordPolicy | undefined>(undefined);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        if (!loggedIn) {
            dispatch<any>(signIn('', '', obt));
        }
    }, []);

    useEffect(() => {
        if (loggedIn) {
            onboardService.onboardingStatus(handleError, handleSuccessStatus);
        }
    }, [loggedIn]);

    useEffect(() => {
        if (loginError?.response?.status === 400) {
            setLoading(false);
            setErrorMessage(translation('LINK_EXPIRED'));
            dispatch(signInFailure({} as AxiosResponse));
            setTimeout(() => basicService.redirect('/signin'), 3000);
            return;
        }
    }, [loginError]);

    const handleSuccessStatus = (response: any) => {
        const results: any = response.data;

        if (results) {
            setPasswordSet(!results.passwordSet);
            setResetPassword(results.passwordReset);
            setSyncSourceSet(results.syncSourceSet);
            setConfigureMfa(results.configureMfa);
            setDisplayName(results.displayName);
            setMfaReset(results.mfaReset);
            setPasswordPolicy(results.passwordPolicy);

            setLoading(false);

            if (results.passwordReset) {
                setOnStep(steps.setPassword);
                return;
            }

            if (!results.syncSourceSet && results.passwordSet && !results.configureMfa && !results.mfaReset) {
                setLoading(true);
                getNewToken();
            }
        }
    };

    const handleError = (error: any) => {
        setErrorMessage(error.response.data.message);
        setIsOnboardingCompleting(false);
    };

    const letsGetStartedClicked = () => {
        // figure out which component to show
        if (resetPassword) {
            setOnStep(steps.setPassword);
        } else if (syncSourceSet) {
            setOnStep(steps.luckyYou);
        } else if (passwordSet) {
            setOnStep(steps.setPassword);
        } else if (configureMfa || mfaReset) {
            setOnStep(steps.setMfa);
        } else {
            setCompleted();
        }
    };

    const setPasswordClicked = () => {
        if (configureMfa || mfaReset) {
            setOnStep(steps.setMfa);
        } else {
            setCompleted();
        }
    };

    const setExpiredPasswordClicked = () => {
        if (configureMfa || mfaReset) {
            setOnStep(steps.setMfa);
        } else {
            // when the user updates their expired password, they must login again
            setOnStep(steps.loginAgain);
        }
    };

    const setCompleted = () => {
        setOnStep(steps.summary);
    };

    const handleSuccessNewToken = (response: any) => {
        const token: string = response.data.token;

        if (token) {
            basicService.clearUserLocalStorageData();
            authService.setRefreshToken('1');

            currentUserData.onboarding = false;
            dispatch(currentUser(currentUserData));

            authService.getNewAccessToken(callback);
        }
    };

    // this callback is sent to the getNewAccessToken function and only called after the getNewAccessToken function is finished.
    const callback = () => {
        dispatch<any>(handleReloadShellConfig());

        setTimeout(function () {
            navigateOut();
            setIsOnboardingCompleting(false);
        }, 3000);
    };

    const navigateOut = () => {
        let path: string = '/apps';

        if (!shellConfig.gettingStartedComplete && hasPermission([Administrator])) {
            path = '/getting-started';
        }

        basicService.redirect(path);
    };

    const summaryClicked = () => {
        setIsOnboardingCompleting(true);
        getNewToken();
    };

    const getNewToken = () => {
        // refresh token
        onboardService.getFullToken(handleError, handleSuccessNewToken);
    };

    const luckyYouClicked = () => {
        if (configureMfa || mfaReset) {
            setOnStep(steps.setMfa);
        } else {
            getNewToken();
        }
    };

    const backToWelcome = () => {
        setOnStep(steps.welcome);
    };

    return (
        <div className='login'>
            <>
                <div className='login__logo--left logo__kaseya' />
                <div className='login__wrapper'>
                    <div className='login__container'>
                        {loading && (
                            <div className='onboard__loading-block'>
                                <PageLoader />
                            </div>
                        )}

                        <div className='login__form'>
                            {errorMessage === '' && (
                                <>
                                    {!loading && onStep === steps.welcome && (
                                        <OnboardWelcomeComponent displayName={displayName} letsGetStartedCallback={letsGetStartedClicked} />
                                    )}

                                    {!loading && onStep === steps.setPassword && (
                                        <OnboardSetPasswordComponent
                                            setIsPasswordReset={setIsPasswordReset}
                                            isPasswordReset={isPasswordReset}
                                            isReset={resetPassword}
                                            passwordPolicy={passwordPolicy}
                                            setPasswordCallback={setPasswordClicked}
                                            setExpiredPasswordCallback={setExpiredPasswordClicked}
                                        />
                                    )}

                                    {!loading && onStep === steps.loginAgain && <OnboardLoginAgainComponent />}

                                    {!loading && onStep === steps.setMfa && (
                                        <OnboardSetMfaToken setCompleted={setCompleted} backToWelcome={backToWelcome} />
                                    )}

                                    {!loading && onStep === steps.luckyYou && <OnboardLuckyYouComponent luckyYouCallback={luckyYouClicked} />}

                                    {!loading && onStep === steps.summary && (
                                        <OnboardSummaryComponent summaryCallback={summaryClicked} isOnboardingCompleting={isOnboardingCompleting} />
                                    )}
                                </>
                            )}
                        </div>
                        {errorMessage !== '' && (
                            <>
                                <div className='onboard'>
                                    <div className='onboard--content' tabIndex={0}>
                                        <div className='onboard__line2'>:-( {translation('ONBOARD_SUMMARY_ERROR')}</div>
                                        <div className='onboard__line3'>{errorMessage}</div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <div className='login__footer'>
                        <span className='login__footer--left'>
                            <a href={basicService.getHelpUrl()} title={translation('HELP_AND_SUPPORT')} target='_blank' rel='noopener noreferrer'>
                                {translation('HELP_AND_SUPPORT')}
                            </a>
                        </span>
                        <span className='login__footer--right'>
                            &nbsp;| &copy;
                            {translation('COPYRIGHT')}
                        </span>
                    </div>
                </div>
            </>
        </div>
    );
};

export default OnboardPage;
